import {PointArr} from 'common/src/modules/movieEditor/movieEditor.types';
import {MoviePath} from 'common/src/modules/movie/moviePrimitives.types';

class BezierCurve {
  accuracy = 100;
  lengthsArr: number[] = null;
  length: number = null;
  p0: PointArr = null;
  p1: PointArr = null;
  p2: PointArr = null;
  p3: PointArr = null;

  constructor(p0: PointArr, p1: PointArr, p2: PointArr, p3: PointArr) {
    this.p0 = p0;
    this.p1 = p1;
    this.p2 = p2;
    this.p3 = p3;
  }

  getValueAtTime(time: number, dimension: number) {
    return (
      Math.pow(1 - time, 3) * this.p0[dimension] +
      Math.pow(1 - time, 2) * time * 3 * this.p1[dimension] +
      (1 - time) * Math.pow(time, 2) * 3 * this.p2[dimension] +
      Math.pow(time, 3) * this.p3[dimension]
    );
  }

  getPointAtTime(time: number) {
    return {
      x: this.getValueAtTime(time, 0),
      y: this.getValueAtTime(time, 1),
    };
  }

  getPointAndDerivativeAtTime(t: number) {
    const x = this.getValueAtTime(t, 0);
    const y = this.getValueAtTime(t, 1);
    const dx = this.getDerivativeAtTimeForDimension(t, 0);
    const dy = this.getDerivativeAtTimeForDimension(t, 1);

    return {x, y, dx, dy};
  }

  getLength() {
    if (this.length) return this.length;

    if (!this.lengthsArr) this.lengthsArr = [];
    let prev = this.getPointAtTime(0);
    this.length = 0;

    for (let i = 1; i <= this.accuracy; i++) {
      const t = i / this.accuracy;
      const current = this.getPointAtTime(t);
      const segmentLength = Math.sqrt(
        Math.pow(current.x - prev.x, 2) + Math.pow(current.y - prev.y, 2),
      );
      this.length += segmentLength;
      this.lengthsArr.push(segmentLength);
      prev = current;
    }

    return this.length;
  }

  getValueAtLengthPercent(percent: number) {
    if (!this.length || !this.lengthsArr) this.getLength();
    const lengthToAchieve = this.length * percent;
    let summedLength = 0;

    for (let i = 0; i < this.accuracy; i++) {
      const segmentLength = this.lengthsArr[i];
      summedLength += segmentLength;

      if (summedLength >= lengthToAchieve) {
        const overflow = summedLength - lengthToAchieve;
        const percent = overflow / segmentLength;

        return (i + percent) / this.accuracy;
      }
    }

    return 1;
  }

  getCriticalPointsByDimension(dimension: number) {
    const a =
      -3 * this.p0[dimension] +
      9 * this.p1[dimension] -
      9 * this.p2[dimension] +
      3 * this.p3[dimension];
    const b = 6 * this.p0[dimension] - 12 * this.p1[dimension] + 6 * this.p2[dimension];
    const c = -3 * this.p0[dimension] + 3 * this.p1[dimension];
    const v1 = ((-b + Math.sqrt(Math.pow(b, 2) - 4 * a * c)) / 2) * a;
    const v2 = ((-b - Math.sqrt(Math.pow(b, 2) - 4 * a * c)) / 2) * a;
    const res = [this.p0[dimension], this.p3[dimension]];

    if (!isNaN(v1)) {
      res.push(v1);
    }
    
    if (!isNaN(v1)) {
      res.push(v2);
    }

    return res;
  }

  getCriticalPoints() {
    return {
      x: this.getCriticalPointsByDimension(0),
      y: this.getCriticalPointsByDimension(1),
    };
  }

  getSubBezierByDim(time: number, dimension: number, isStart: boolean) {
    const q0 = (1 - time) * this.p0[dimension] + time * this.p1[dimension];
    const q1 = (1 - time) * this.p1[dimension] + time * this.p2[dimension];
    const q2 = (1 - time) * this.p2[dimension] + time * this.p3[dimension];
    const c0 = (1 - time) * q0 + time * q1;
    const c1 = (1 - time) * q1 + time * q2;
    const x = (1 - time) * c0 + time * c1;

    return isStart ? [x, c1, q2, this.p3[dimension]] : [this.p0[dimension], q0, c0, x];
  }

  getSubBezier(time: number, isStart: boolean) {
    const x = this.getSubBezierByDim(time, 0, isStart);
    const y = this.getSubBezierByDim(time, 1, isStart);

    return new BezierCurve([x[0], y[0]], [x[1], y[1]], [x[2], y[2]], [x[3], y[3]]);
  }

  draw(ctx: CanvasRenderingContext2D) {
    ctx.bezierCurveTo(this.p1[0], this.p1[1], this.p2[0], this.p2[1], this.p3[0], this.p3[1]);
  }

  getBoundaries(dim: number) {
    const a = -3 * this.p0[dim] + 9 * this.p1[dim] + -9 * this.p2[dim] + 3 * this.p3[dim];
    const b = 6 * this.p0[dim] - 12 * this.p1[dim] + 6 * this.p2[dim];
    const c = -3 * this.p0[dim] + 3 * this.p1[dim];

    const v1 = (-b + Math.sqrt(b ** 2 - 4 * a * c)) / (2 * a);
    const v2 = (-b - Math.sqrt(b ** 2 - 4 * a * c)) / (2 * a);

    const minmax1 = this.getValueAtTime(Math.min(1, Math.max(0, v1)), dim);
    const minmax2 = this.getValueAtTime(Math.min(1, Math.max(0, v2)), dim);

    const res = [this.getValueAtTime(0, dim), this.getValueAtTime(1, dim)];
    if (!isNaN(minmax1)) res.push(minmax1);
    if (!isNaN(minmax2)) res.push(minmax2);

    return res;
  }

   getSlopeAtTime(time: number, dim: number) {
    return (
      3 * Math.pow(1 - time, 2) * (this.p1[dim] - this.p0[dim]) +
      6 * (1 - time) * time * (this.p2[dim] - this.p1[dim]) +
      3 * Math.pow(time, 2) * (this.p3[dim] - this.p2[dim])
    );
  }

  getTimeAtValue(dim: number, value: number) {
    const t0 = this.getValueAtTime(0, dim),
      t1 = this.getValueAtTime(0, dim);
    const minVal = Math.min(t0, t1),
      maxVal = Math.max(t0, t1);

    let t = 0.5;

    if (maxVal > value && minVal < value) {
      t = (value - minVal) / (maxVal - minVal);
      if (t0 > t1) t = 1 - t;
    }

    for (let i = 0; i < 20; i++) {
      const slope = this.getSlopeAtTime(t, dim);
      if (slope === 0) break;
      const a = this.getValueAtTime(t, dim) - this.getSlopeAtTime(t, dim) * t;
      t = (value - a) / this.getSlopeAtTime(t, dim);
    }

    return t;
  }

  private getDerivativeAtTimeForDimension(t: number, dimension: number) {
    const p0 = this.p0[dimension];
    const p1 = this.p1[dimension];
    const p2 = this.p2[dimension];
    const p3 = this.p3[dimension];

    const t2 = t * t;
    const mt = 1 - t;
    const mt2 = mt * mt;

    return (
      3 * mt2 * (p1 - p0) +
      6 * mt * t * (p2 - p1) +
      3 * t2 * (p3 - p2)
    );
  }

  static fromPath(path: MoviePath): BezierCurve[] {
    const {
      c: closed,
      val: {v, o, i},
    } = path;
    const curves: BezierCurve[] = v
      .slice(1)
      .map(
        ([x, y], idx) =>
          new BezierCurve(
            [v[idx][0], v[idx][1]],
            [o[idx][0] + v[idx][0], o[idx][1] + v[idx][1]],
            [i[idx + 1][0] + x, i[idx + 1][1] + y],
            [x, y],
          ),
      );

    if (closed) {
      const lastIdx = v.length - 1;
      curves.push(
        new BezierCurve(
          [v[lastIdx][0], v[lastIdx][1]],
          [v[lastIdx][0] + o[lastIdx][0], v[lastIdx][1] + o[lastIdx][1]],
          [v[0][0] + i[0][0], v[0][1] + i[0][1]],
          [v[0][0], v[0][1]],
        ),
      );
    }

    return curves;
  }
}

export default BezierCurve; 